import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  X, 
  Download, 
  FileText, 
  CheckCircle2, 
  Calendar, 
  Building2,
  FlaskConical,
  ExternalLink,
  ZoomIn,
  ZoomOut,
  Printer
} from 'lucide-react';
import { Button } from './ui/button';
import COADocument from './COADocument';

/**
 * TDS Modal Component
 * Displays Technical Data Sheet in a centered overlay modal
 * Supports both PDF preview and COA details view
 * 
 * Features:
 * - ESC key to close
 * - Click outside to close
 * - Mobile-friendly close button with safe area insets
 * - Focus trap for accessibility
 */
const COAModal = ({ isOpen, onClose, coa, product }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [viewMode, setViewMode] = useState('document'); // 'document' | 'pdf' | 'details'
  const printRef = useRef(null);

  // Handle ESC key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !coa) return null;

  const handleDownload = () => {
    const url = coa.pdf_url || coa.coa_url;
    if (url) {
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.download = `TDS-${coa.product_name || 'Certificate'}-${coa.batch_number || ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printContent = printRef.current.innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>TDS - ${coa.product_name || 'Certificate'} - ${coa.batch_number || ''}</title>
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const coaUrl = coa.pdf_url || coa.coa_url;
  const isPDF = coaUrl?.toLowerCase().endsWith('.pdf');
  const isImage = coaUrl?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const hasExternalDoc = coaUrl && (isPDF || isImage);

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleBackdropClick}
      data-testid="coa-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="coa-modal-title"
    >
      {/* Modal Container - relative positioning for absolute close button */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl w-full overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col ${
          isZoomed ? 'max-w-6xl max-h-[90vh]' : 'max-w-4xl max-h-[85vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mobile Close Button - Floating, always visible */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-50 w-10 h-10 flex items-center justify-center bg-white/95 hover:bg-slate-100 rounded-full shadow-lg border border-slate-200 transition-colors sm:hidden"
          style={{ 
            top: 'max(8px, env(safe-area-inset-top, 8px))',
            right: 'max(8px, env(safe-area-inset-right, 8px))'
          }}
          aria-label="Close modal"
          data-testid="coa-modal-close-mobile"
        >
          <X className="w-5 h-5 text-slate-600" />
        </button>

        {/* Header */}
        <div className="sticky top-0 z-20 bg-white border-b border-slate-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between"
          style={{ paddingTop: 'max(12px, env(safe-area-inset-top, 12px))' }}
        >
          <div className="flex items-center gap-2 sm:gap-3 pr-10 sm:pr-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-cyan-50 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
            </div>
            <div className="min-w-0">
              <h2 id="coa-modal-title" className="font-heading font-semibold text-base sm:text-lg text-slate-900 truncate">
                {'Technical Data Sheet'}
              </h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate">
                {coa.product_name || product?.name || 'Product'} • {'Batch'} {coa.batch_number || 'N/A'}
              </p>
            </div>
          </div>
          
          {/* Desktop buttons */}
          <div className="hidden sm:flex items-center gap-2">
            {/* View Mode Toggle */}
            {hasExternalDoc && (
              <div className="flex items-center bg-slate-100 rounded-lg p-1 mr-2">
                <button
                  onClick={() => setViewMode('document')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'document' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Generated TDS
                </button>
                <button
                  onClick={() => setViewMode('pdf')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    viewMode === 'pdf' 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  Original PDF
                </button>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsZoomed(!isZoomed)}
              className="text-slate-500 hover:text-slate-700"
              title={isZoomed ? 'Zoom out' : 'Zoom in'}
            >
              {isZoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-500 hover:text-slate-700"
              data-testid="coa-modal-close"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {/* Generated TDS Document View */}
          {viewMode === 'document' && (
            <div ref={printRef} className="bg-slate-50">
              <COADocument coa={coa} product={product} />
            </div>
          )}

          {/* PDF/Image Preview Section */}
          {viewMode === 'pdf' && hasExternalDoc && (
            <div className={`bg-slate-100 ${isZoomed ? 'min-h-[500px]' : 'min-h-[400px]'}`}>
              {isPDF ? (
                <iframe
                  src={`${coaUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                  className="w-full h-full min-h-[inherit]"
                  style={{ minHeight: isZoomed ? '600px' : '500px' }}
                  title={`TDS - ${coa.product_name}`}
                />
              ) : (
                <div className="flex items-center justify-center p-4">
                  <img
                    src={coaUrl}
                    alt={`TDS - ${coa.product_name}`}
                    className={`max-w-full object-contain ${isZoomed ? 'max-h-[600px]' : 'max-h-[400px]'}`}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            For institutional laboratory use only. Not for consumer use.
          </p>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-6"
            >
              Close
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              className="px-4"
              data-testid="coa-modal-print"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            {coaUrl && (
              <Button
                onClick={handleDownload}
                className="bg-slate-900 hover:bg-slate-800 px-6"
                data-testid="coa-modal-download"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default COAModal;
